// filepath: /Users/ephraim/yesodcopilot/client/src/components/group/EventCard.js
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faEllipsisV, 
  faEdit, 
  faTrash, 
  faLink, 
  faCopy, 
  faTimes,
  faExclamationTriangle,
  faCalendarAlt,
  faBookOpen // Changed from faBook to faBookOpen for open book icon
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import StripePaymentModal from '../modals/StripePaymentModal';
import AttendeeModal from '../modals/AttendeeModal';
import { createShareLinks } from '../../config';
import { copyToClipboardEnhanced } from '../../utils/linkUtils';
import groupService from '../../api/groupService';
import '../../styles/group/EventCard.css';

// Utility function to check if an event/class is past (3 hours after start time)
// Recurring events/classes never become "past"
const isEventPast = (eventDate, isRecurring = false) => {
  // If it's a recurring event/class, it never becomes past
  if (isRecurring) {
    return false;
  }
  
  const now = new Date();
  const eventStart = new Date(eventDate);
  const threeHoursAfterEvent = new Date(eventStart.getTime() + (3 * 60 * 60 * 1000)); // Add 3 hours
  return now > threeHoursAfterEvent;
};

// Utility function to format recurrence pattern for display
const formatRecurrenceTag = (isRecurring, recurrencePattern, isClass) => {
  if (!isRecurring || !recurrencePattern || recurrencePattern === 'none') {
    return isClass ? 'Class' : 'Event';
  }
  
  // Map recurrence patterns to user-friendly short forms (removed monthly)
  const patternMap = {
    'daily': 'Daily',
    'weekly': 'Weekly', 
    'biweekly': 'Bi-weekly'
  };
  
  const formattedPattern = patternMap[recurrencePattern.toLowerCase()] || 
                          recurrencePattern.charAt(0).toUpperCase() + recurrencePattern.slice(1);
  
  return `${formattedPattern} ${isClass ? 'Class' : 'Event'}`;
};

const EventCard = ({ event, groupId, isAdmin, onEditEvent, onDeleteEvent, onEditClass, isClass = false }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // New state for delete confirmation modal
  const [showAttendeeModal, setShowAttendeeModal] = useState(false); // New state for attendee modal
  const [copySuccess, setCopySuccess] = useState(false);
  const [localAttendeeCount, setLocalAttendeeCount] = useState(event.attendees_count || 0);
  const [attendeesData, setAttendeesData] = useState([]);
  const menuRef = useRef(null);
  const modalRef = useRef(null);
  const deleteModalRef = useRef(null); // New ref for delete modal
  const shareUrlRef = useRef(null);
  const { currentUser } = useAuth();
  const isComponentMounted = useRef(true);
  
  // Process image URL to handle special characters in Supabase URLs
  const processImageUrl = (url) => {
    if (!url) return null;
    
    // Check if the url is not a string (e.g., it's an object)
    if (typeof url !== 'string') {
      console.error('Invalid URL type:', typeof url, url);
      return null;
    }
    
    try {
      // First try to create a valid URL object to see if it's parseable
      try {
        new URL(url);
      } catch (e) {
        console.error('Invalid URL format:', url);
        return null;
      }
      
      // For Supabase storage URLs, don't add cache busting parameters
      // as they can cause HTTP/2 protocol errors
      if (url.includes('supabase.co/storage/v1/object/public/')) {
        // Just return the clean URL - Supabase handles caching appropriately
        return url;
      }
      
      // For other URLs, we can still add cache busting if needed
      // But most modern CDNs handle caching correctly
      return url;
      
    } catch (error) {
      console.error('Error processing image URL:', error);
      return null;
    }
  };
  
  // Generate event URL
  const generateEventUrl = () => {
    // For classes, we should eventually use a different URL pattern, 
    // but for now we'll reuse the event URL structure
    return `${window.location.origin}/group/${groupId}/events/${event.id}`;
  };

  // Generate event URL for sharing
  const generateShareUrl = () => {
    if (isClass) {
      return createShareLinks.class(event.id);
    } else {
      return createShareLinks.event(event.id);
    }
  };

  // Format date for display in the new format - e.g., "THU 26 March, 8:00 PM"
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const weekday = date.toLocaleString('default', { weekday: 'short' }).toUpperCase();
    return `${weekday} ${day} ${month}`;
  };

  // Format time - e.g., "8:00 PM"
  const formatTime = (dateString) => {
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  // Format recurring event display
  const formatRecurringDisplay = (isRecurring, recurrencePattern, recurrenceDay, dateString) => {
    if (!isRecurring || !recurrencePattern || recurrencePattern === 'none') {
      return null; // Use normal date display
    }
    
    const time = formatTime(dateString);
    
    if (recurrencePattern === 'daily') {
      return `Every Day at ${time}`;
    } else if (recurrencePattern === 'weekly' && recurrenceDay) {
      const days = {
        'monday': 'Monday',
        'tuesday': 'Tuesday', 
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      const dayName = days[recurrenceDay.toLowerCase()] || recurrenceDay;
      return `Every ${dayName} at ${time}`;
    }
    
    return null; // Fallback to normal date display
  };

  // Handle card click to navigate to event details page
  const handleCardClick = (e) => {
    // Prevent navigation if any modal is open or menu is open
    if (showMenu || showDeleteModal || showSponsorModal || showShareModal || showAttendeeModal) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // Check if the click is on an interactive element that should not trigger navigation
    const target = e.target;
    const isInteractiveElement = target.closest('.event-action-menu-container') ||
                                target.closest('.event-action-menu') ||
                                target.closest('.sponsor-button') ||
                                target.closest('.delete-modal') ||
                                target.closest('.event-action-item');
    
    if (isInteractiveElement) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    if (isClass) {
      // Navigate to class details page
      navigate(`/group/${groupId}/classes/${event.id}`, {
        state: { fromAdmin: isAdmin }
      });
    } else {
      // Navigate to event details page  
      navigate(`/group/${groupId}/events/${event.id}`, {
        state: { fromAdmin: isAdmin }
      });
    }
  };

  // Toggle the action menu
  const toggleMenu = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Handle edit action
  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    
    if (isClass) {
      // If this is a class, use the onEditClass handler if provided
      if (onEditClass) {
        onEditClass(event);
      } else if (onEditEvent) {
        // Fallback to onEditEvent if no specific class handler
        onEditEvent(event);
      }
    } else {
      // For regular events, use onEditEvent
      if (onEditEvent) {
        onEditEvent(event);
      }
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = (e) => {
    if (e) e.stopPropagation();
    setShowDeleteModal(false);
  };

  // Confirm deletion
  const confirmDelete = (e) => {
    e.stopPropagation();
    if (onDeleteEvent) {
      // Make sure we're using the correct ID and type
      console.log(`Confirming deletion of ${isClass ? 'class' : 'event'} with ID: ${event.id}`);
      
      // Add a small delay to ensure any pending API calls are completed first
      setTimeout(() => {
        onDeleteEvent(event.id);
      }, 100);
    }
    setShowDeleteModal(false);
  };

  // Handle share action - now directly copies link
  const handleCopyLink = async (e) => {
    e.stopPropagation();
    setShowMenu(false);
    
    const shareUrl = generateShareUrl();
    
    try {
      // Use the browser's clipboard API directly
      await navigator.clipboard.writeText(shareUrl);
      
      // Show a toast notification
      showCopyToast();
      
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          showCopyToast();
        } else {
          console.error('Failed to copy link');
        }
      } catch (fallbackErr) {
        console.error('Copy to clipboard failed:', fallbackErr);
      }
    }
  };

  // Show copy success toast
  const showCopyToast = () => {
    // Remove any existing toast
    const existingToast = document.querySelector('.copy-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create and show new toast
    const toast = document.createElement('div');
    toast.className = 'copy-toast';
    toast.textContent = 'Link copied!';
    document.body.appendChild(toast);

    // Remove toast after animation
    setTimeout(() => {
      if (toast && toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 3000);
  };

  // Handle sponsor action
  const handleSponsor = (e) => {
    e.stopPropagation();
    setShowSponsorModal(true);
  };

  // Close sponsor modal with proper event handling
  const handleCloseSponsorModal = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setShowSponsorModal(false);
  };

  // Handle attendee count click to show attendee modal
  const handleAttendeeCountClick = (e) => {
    e.stopPropagation();
    // Only show modal for admins
    if (isAdmin) {
      setShowAttendeeModal(true);
    }
  };

  // Open attendee modal from menu
  const handleOpenAttendeeModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(false); // Close the menu
    setShowAttendeeModal(true);
  };

  // Close attendee modal
  const handleCloseAttendeeModal = () => {
    setShowAttendeeModal(false);
  };

  // Fetch the latest RSVP count
  const fetchLatestRsvpCount = async () => {
    if (!isComponentMounted.current) return;

    try {
      // Only fetch if we have valid IDs
      if (groupId && event && event.id) {
        if (isClass) {
          // For classes, use class-specific endpoints
          const response = await groupService.getClass(groupId, event.id);
          
          if (response && response.data) {
            const count = response.data.attendees_count || 0;
            setLocalAttendeeCount(count);
          }

          // Get enrollment details for avatars
          const enrollmentResponse = await groupService.getClassEnrollments(groupId, event.id);
          
          if (enrollmentResponse && enrollmentResponse.data && Array.isArray(enrollmentResponse.data)) {
            const enrolledStudents = enrollmentResponse.data.filter(enrollment => enrollment.status === 'enrolled');
            setAttendeesData(enrolledStudents.slice(0, 3));
            
            // Calculate total attendees by summing up attendee_count from each enrollment
            const totalAttendees = enrolledStudents.reduce((total, enrollment) => {
              const attendeeCount = enrollment.attendee_count ? Number(enrollment.attendee_count) : 1;
              return total + attendeeCount;
            }, 0);
            
            setLocalAttendeeCount(totalAttendees);
          }
        } else {
          // For events, use event-specific endpoints
          const response = await groupService.getEvent(groupId, event.id);
          
          if (response && response.data) {
            const count = response.data.attendees_count || 0;
            setLocalAttendeeCount(count);
          }

          // Get RSVP details for avatars
          const rsvpResponse = await groupService.getEventRsvps(groupId, event.id);
          
          if (rsvpResponse && rsvpResponse.data && Array.isArray(rsvpResponse.data)) {
            const goingRsvps = rsvpResponse.data.filter(rsvp => rsvp.status === 'going');
            setAttendeesData(goingRsvps.slice(0, 3));
            
            // Calculate total attendees by summing up attendee_count from each RSVP
            const totalAttendees = goingRsvps.reduce((total, rsvp) => {
              const attendeeCount = rsvp.attendee_count ? Number(rsvp.attendee_count) : 1;
              return total + attendeeCount;
            }, 0);
            
            setLocalAttendeeCount(totalAttendees);
          }
        }
      }
    } catch (error) {
      // Only log errors if they're significant
      if (error.response?.status !== 404) {
        console.error('[EventCard] Error fetching RSVP data:', error.message);
      }
      
      // If we get a 404 for an event endpoint but this is supposed to be a class,
      // it might be a state synchronization issue
      if (error.response?.status === 404 && !isClass) {
        try {
          const classResponse = await groupService.getClass(groupId, event.id);
          if (classResponse && classResponse.data) {
            setLocalAttendeeCount(classResponse.data.attendees_count || 0);
            return;
          }
        } catch (classError) {
          // Silent fallback
        }
      }
      
      // Set reasonable fallbacks
      setLocalAttendeeCount(event.attendees_count || 0);
      setAttendeesData([]);
    }
  };

  // Close share modal
  const handleCloseShareModal = (e) => {
    if (e) e.stopPropagation();
    setShowShareModal(false);
    setCopySuccess(false); // Reset copy success when closing modal
  };

  // Handle overlay click (clicking outside the modal content)
  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on modal content
    if (e.target === e.currentTarget) {
      handleCloseShareModal(e);
    }
  };

  // Close menu when clicking outside - improved logic
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle action menu
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }

      // Handle delete modal
      if (showDeleteModal && deleteModalRef.current && !deleteModalRef.current.contains(event.target)) {
        setShowDeleteModal(false);
      }
      
      // DON'T handle share modal here - it's handled by the overlay click
      // This prevents the flashing issue when moving mouse away from card
    };

    // Only add the listener if we have modals that need it (not the share modal)
    if (showMenu || showDeleteModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, showDeleteModal]); // Removed showShareModal from dependencies

  // Enhanced error handling for the image
  const handleImageError = () => {
    console.error(`Failed to load image for ${isClass ? 'class' : 'event'} ${event.id || 'unknown'}:`, {
      originalUrl: event.cover_image_url,
      processedUrl: processImageUrl(event.cover_image_url),
      eventData: {
        id: event.id,
        title: event.title,
        isClass: isClass
      }
    });
    
    // Try to identify if the URL is from Supabase storage
    if (event.cover_image_url && event.cover_image_url.includes('storage.googleapis.com')) {
      console.warn('Detected Supabase storage URL. The issue may be related to object permissions or URL encoding.');
    }
    
    setImageError(true);
  };

  // Helper function to get user initials
  const getUserInitials = (name) => {
    if (!name) return '?';
    const nameParts = name.split(' ');
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
    return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
  };

  useEffect(() => {
    isComponentMounted.current = true;
    // Note: fetchLatestRsvpCount() removed - data already comes from props
    // and is set in the useEffect below when event.attendees_count changes
    
    // Listen for custom RSVP count update events
    const handleRsvpCountUpdate = (customEvent) => {
      const { eventId: updatedEventId, newCount } = customEvent.detail;
      
      // Only update if this is the same event/class
      if (updatedEventId === event.id) {
        console.log(`[EventCard] Received RSVP count update for ${isClass ? 'class' : 'event'} ${event.id}: ${newCount}`);
        setLocalAttendeeCount(newCount);
      }
    };
    
    document.addEventListener('rsvpCountUpdated', handleRsvpCountUpdate);
    
    return () => {
      isComponentMounted.current = false;
      document.removeEventListener('rsvpCountUpdated', handleRsvpCountUpdate);
    };
  }, [event.id, groupId, isClass]);

  // Reset the error state and update attendee count when event ID changes
  useEffect(() => {
    setImageError(false);
    
    // Set imageError to true immediately if there's no valid cover image URL
    if (!event.cover_image_url) {
      console.log(`${isClass ? 'Class' : 'Event'} ${event.id} has no cover_image_url, using placeholder`);
      setImageError(true);
    } else {
      console.log(`${isClass ? 'Class' : 'Event'} ${event.id} has cover_image_url:`, event.cover_image_url);
    }

    // Update local attendee count when event data changes
    setLocalAttendeeCount(event.attendees_count || 0);
  }, [event.id, event.cover_image_url, event.attendees_count, isClass]); // Only depend on specific values, not entire event object

  // Handle sponsor submit with refresh of RSVP data
  const handleSponsorSubmit = (sponsorData) => {
    console.log("Sponsorship submitted:", sponsorData);
    fetchLatestRsvpCount();
  };

  // Check if the event/class is past
  const isPast = isEventPast(event.date, event.is_recurring);

  return (
    <div className={`event-card ${isPast ? 'event-card-past' : ''}`} onClick={handleCardClick}>
      {/* Cover image section */}
      <div className="event-card-image-container">
        {/* Card type tag in top left corner */}
        <div className="event-card-type-tag">
          {formatRecurrenceTag(event.is_recurring, event.recurrence_pattern, isClass)}
        </div>

        {/* Past event overlay */}
        {isPast && (
          <div className="event-card-past-overlay">
            <span className="past-label">Past</span>
          </div>
        )}

        {!imageError ? (
          <img 
            className="event-card-img" 
            src={processImageUrl(event.cover_image_url)}
            alt={event.title}
            onError={handleImageError}
          />
        ) : (
          <div className="event-card-placeholder" style={{
            backgroundColor: isClass ? '#4682B4' : '#DAA520', // Light blue for classes, Dark golden/yellow for events
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '2.5rem'
          }}>
            <FontAwesomeIcon icon={isClass ? faBookOpen : faCalendarAlt} />
          </div>
        )}
        
        {/* Sponsors badge remains at the top right corner if applicable */}
        {event.allow_sponsorship && event.sponsors_count > 0 && (
          <div className="event-sponsors-badge">
            <FontAwesomeIcon icon={faUsers} />
            <span>{event.sponsors_count} Sponsors</span>
          </div>
        )}

        {/* Admin actions menu (three dots) */}
        {isAdmin && (
          <div className="event-action-menu-container" ref={menuRef}>
            <button className="event-action-menu-button" onClick={toggleMenu}>
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>
            
            {showMenu && (
              <div className="event-action-menu">
                {!isClass && (
                  <button className="event-action-item" onClick={handleOpenAttendeeModal}>
                    <FontAwesomeIcon icon={faUsers} /> Attendees
                  </button>
                )}
                <button className="event-action-item" onClick={handleCopyLink}>
                  <FontAwesomeIcon icon={faLink} /> Share
                </button>
                <button className="event-action-item" onClick={handleEdit}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="event-action-item delete" onClick={openDeleteModal}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Content section with clean white background */}
      <div className="event-card-content">
        {/* Event title with price tag */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="event-card-title flex-1">{event.title}</h3>
          {/* Price tag - only show if event has a ticket price */}
          {event.ticket_price_cents && event.ticket_price_cents > 0 && (
            <div className="inline-flex items-center px-3 py-1 bg-green-50 border border-green-200 rounded-lg flex-shrink-0">
              <span className="text-sm font-semibold text-green-700">
                ${(event.ticket_price_cents / 100).toFixed(2)}
              </span>
            </div>
          )}
        </div>
        
        {/* Date and time with category/type */}
        <div className="event-card-datetime">
          {(() => {
            const recurringDisplay = formatRecurringDisplay(event.is_recurring, event.recurrence_pattern, event.recurrence_day, event.date);
            return recurringDisplay || `${formatDate(event.date)}, ${formatTime(event.date)}`;
          })()}
          {event.type && <span className="event-card-type"> | {event.type}</span>}
          {/* For classes, optionally show instructor if available */}
          {isClass && event.teacher && <span className="event-card-type"> | {event.teacher}</span>}
        </div>
        
        {/* Event description */}
        <p className="event-card-description">
          {event.description && event.description.length > 100 
            ? `${event.description.substring(0, 100)}...` 
            : event.description}
        </p>
        
        {/* Action area: Sponsor button and attendance */}
        <div className={`event-card-actions ${!event.allow_sponsorship ? 'no-sponsor' : ''}`}>
          {/* Sponsor button - only show if sponsorship is allowed */}
          {event.allow_sponsorship && (
            <button className="sponsor-button" onClick={handleSponsor}>
              {isClass ? 'Dedicate' : 'Sponsor'}
            </button>
          )}
          
          {/* Attendance count with profile images - only for events, not classes */}
          {!isClass && (
            <div className="event-card-attendees">
              <div 
                className="attendees-count clickable" 
                onClick={handleAttendeeCountClick}
                title="Click to view attendee list"
              >
                {localAttendeeCount === 1 ? '1 Attendee' : `${localAttendeeCount} Attendees`}
              </div>
            <div className="attendees-avatars">
              {/* Show 3 gray placeholder icons when no attendees */}
              {attendeesData.length > 0 ? (
                attendeesData.map((attendee, index) => (
                  <div 
                    key={index}
                    className={`attendee-avatar ${!attendee.profile_image ? '' : ''}`}
                    style={{
                      backgroundImage: attendee.profile_image ? 
                        `url(${processImageUrl(attendee.profile_image)})` : 
                        'none'
                    }}
                  >
                    {!attendee.profile_image && attendee.name && (
                      <div className="attendee-avatar-initials">
                        {getUserInitials(attendee.name)}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Three gray placeholders when no attendees
                <>
                  <div className="attendee-avatar empty-avatar"></div>
                  <div className="attendee-avatar empty-avatar"></div>
                  <div className="attendee-avatar empty-avatar"></div>
                </>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal - Rendered using Portal */}
      {showDeleteModal && ReactDOM.createPortal(
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal" ref={deleteModalRef} onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
              <h3>Delete {isClass ? 'Class' : 'Event'}</h3>
            </div>
            <div className="delete-modal-content">
              <p>Are you sure you want to delete "{event.title}"?</p>
              <p className="delete-warning">This action cannot be undone.</p>
              
              <div className="delete-modal-actions">
                <button className="btn-cancel" onClick={closeDeleteModal}>
                  Cancel
                </button>
                <button className="btn-delete" onClick={confirmDelete}>
                  Delete {isClass ? 'Class' : 'Event'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Stripe Payment Modal */}
      <StripePaymentModal
        isOpen={showSponsorModal}
        onClose={handleCloseSponsorModal}
        groupId={groupId}
        eventId={isClass ? null : event.id}
        classId={isClass ? event.id : null}
        eventTitle={event.title}
        isClass={isClass}
        onPaymentSuccess={(data) => {
          console.log('Payment successful:', data);
          // You can add additional success handling here
          // like refreshing the event data or showing a success message
        }}
      />

      {/* Attendee Modal */}
      <AttendeeModal
        isOpen={showAttendeeModal}
        onClose={handleCloseAttendeeModal}
        groupId={groupId}
        eventId={event.id}
        event={event}
        isClass={isClass}
      />
    </div>
  );
};

export default EventCard;